import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useStores } from '../../common/hooks';
import { QuestionnaireRouteParams } from '../../models/route-params';
import { RoutePaths } from '../../route-paths';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { ContactInformationSection } from './components/ContactInformationSection';
import { EventAttendeesSection } from './components/EventAttendeesSection';
import { UploadLogoSection } from './components/UploadLogoSection';
import { UploadMediaSection } from './components/UploadMediaSection';
import './ReviewQuestionnairePage.scss';

interface ReviewQuestionnairePageProps {
  onSubmitQuestionnaireSuccess(): void;
}

const ReviewQuestionnairePageImpl: React.FC<ReviewQuestionnairePageProps> = (props) => {
  const {
    onSubmitQuestionnaireSuccess
  } = props;

  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    reviewQuestionnairePageUiStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    questionnaireId,
  } = useParams<QuestionnaireRouteParams>();

  const navigate = useNavigate();

  const handleSubmitQuestionnaire = async (): Promise<void> => {
    if (!reviewQuestionnairePageUiStore.questionnaire) return;

    setIsLoading(true);
    try {
      await apiStore.submitQuestionnaire(instance, reviewQuestionnairePageUiStore.questionnaire.questionnaireId);
      if (reviewQuestionnairePageUiStore.questionnaire.status === 'Submitted') {
        navigate(RoutePaths.Home, { replace: true });
      }
      else {
        onSubmitQuestionnaireSuccess();
        navigate(RoutePaths.AwardResources, { replace: true });
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (!questionnaireId || !awardStore.defaultProgramRef) {
      setIsLoading(false);
      return;
    }

    const currentQuestionnaireId = Number(questionnaireId);
    const awardedNominations = await apiStore.getAwardedNominations(instance);
    reviewQuestionnairePageUiStore.setAwardedNominations(awardedNominations);

    const questionnaire = await apiStore.getQuestionnaire(instance, currentQuestionnaireId);
    reviewQuestionnairePageUiStore.setQuestionnaire(questionnaire);

    //TODO - use get winner events by winner Id API
    const winnerEvents = await apiStore.getWinnerEvents(instance, awardStore.defaultProgramRef.programId);
    reviewQuestionnairePageUiStore.setWinnerEvents(winnerEvents);

    setIsLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      reviewQuestionnairePageUiStore.resetStates();
    };
  }, []);

  if (isLoading) {
    return (
      <FlexBox className='review-questionnaire-page'>
        <Loading />
      </FlexBox>
    );
  }

  return (
    <FlexBox
      className='review-questionnaire-page'
    >
      <aside>
        <Text
          as='h1'
          weight='semibold'
          size={400}
          block
        >
          Review and confirmation
        </Text>
        <Text block as='p'>
          Please review the entire submission for the questionnaire.
        </Text>
      </aside>

      <div className='review-questionnaire-page__questionnaire'>
        {
          reviewQuestionnairePageUiStore.questionnaire ? (
            <>
              <FlexBox
                as='section'
                isFullWidth
                content='space-between'
                className='questionnaire-header'
              >
                {
                  reviewQuestionnairePageUiStore.awardTitle &&
                  <Text
                    as='h2'
                    weight='semibold'
                    size={500}
                    block
                  >
                    {reviewQuestionnairePageUiStore.awardTitle}
                  </Text>
                }
                <FlexBox
                  aligns='center'
                  className='questionnaire-header__actions'
                >
                  <NavLink
                    to={`${RoutePaths.Questionnaires}/${reviewQuestionnairePageUiStore.questionnaire.questionnaireId}`}
                    className='secondary-btn'
                    end
                  >
                    Back
                  </NavLink>
                  <Button
                    appearance='primary'
                    disabled={reviewQuestionnairePageUiStore.questionnaire.status === 'Submitted'}
                    onClick={handleSubmitQuestionnaire}
                  >
                    {reviewQuestionnairePageUiStore.questionnaire.status === 'Submitted' ? 'Save changes' : 'Submit'}
                  </Button>
                </FlexBox>
              </FlexBox>
              <ContactInformationSection />
              {
                reviewQuestionnairePageUiStore.winnerRole === 'Winner' && reviewQuestionnairePageUiStore.winnerEvents.length > 0 &&
                <EventAttendeesSection />
              }
              {
                reviewQuestionnairePageUiStore.winnerRole === 'Winner' && (
                  <>
                    <UploadLogoSection />
                    <UploadMediaSection />
                  </>
                )
              }
            </>
          ) : (
            <Text as='p' block>Sorry, questionnaire could not be found.</Text>
          )
        }
      </div>
    </FlexBox>
  );
}

export const ReviewQuestionnairePage = observer(ReviewQuestionnairePageImpl);
